import { useDeleteRole, useGetRoleDetail } from 'api/services/rolesService';
import { DarwinModal } from 'components/darwin-ui/Modal';
import { useTranslation } from 'react-i18next';
import { extractQueryData } from 'utils/services';
import { DarwinFlex } from 'components/darwin-ui';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useUI } from 'contexts/UiContext';
import { ErrorType } from 'types/genericTypes';

interface DeleteRoleModalProps {
  open: boolean;
  onCancel: () => void;
  roleId?: string;
}

export const DeleteRoleModal = ({
  open,
  onCancel,
  roleId,
}: DeleteRoleModalProps) => {
  const { selectedCompany } = useUI();
  const { t } = useTranslation();
  const { mutateAsync: deleteRole, isPending } = useDeleteRole();
  const { openNotification } = useFloatNotification();
  const companyId = selectedCompany || '';

  const roleDetailsQuery = useGetRoleDetail({
    companyId,
    roleId: roleId || '',
  });
  const roleDetails = extractQueryData(roleDetailsQuery, undefined);

  const hasUsersAssigned = roleDetails?.userCompanyRoles
    ? roleDetails?.userCompanyRoles?.length > 0
    : false;

  const handleDelete = async () => {
    try {
      await deleteRole({
        companyId,
        roleId: roleId || '',
      });

      openNotification(
        t('roles.roles-list.delete-role-modal.success-notification-title'),
        t('roles.roles-list.delete-role-modal.success-notification-message'),
        'success'
      );
    } catch (err) {
      const apiError = err as ErrorType;
      if (apiError?.status === 409) {
        openNotification(
          t('roles.roles-list.delete-role-modal.error-notification-title'),
          t(apiError.body.errorCode),
          'error'
        );
      } else {
        openNotification(
          t('roles.roles-list.delete-role-modal.error-notification-title'),
          t('roles.roles-list.delete-role-modal.error-notification-message'),
          'error'
        );
      }
    }

    onCancel();
  };

  return (
    <DarwinModal
      loading={roleDetailsQuery.isLoading || !roleDetails}
      open={open}
      onCancel={onCancel}
      onOk={hasUsersAssigned ? onCancel : handleDelete}
      title={t('roles.roles-list.delete-role-modal.title')}
      cancelText={t('roles.roles-list.delete-role-modal.cancel-button')}
      okText={
        hasUsersAssigned
          ? t('roles.roles-list.delete-role-modal.assigned-users-error-button')
          : t('roles.roles-list.delete-role-modal.confirm-button')
      }
      confirmLoading={isPending}
      centered
      footer={
        hasUsersAssigned
          ? (_, { OkBtn }) => (
              <DarwinFlex justify="center" align="center">
                <OkBtn />
              </DarwinFlex>
            )
          : undefined
      }
    >
      {hasUsersAssigned
        ? t('roles.roles-list.delete-role-modal.assigned-users-error')
        : t('roles.roles-list.delete-role-modal.body')}
    </DarwinModal>
  );
};
